//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { type SxProps, type Theme } from '@mui/material/styles';

interface PluginSurfaceFrameProps {
  url: string;
  title: string;
  sx?: SxProps<Theme>;
  loadingLabel?: string;
}

/**
 * Renders a plugin's HTML surface inside a same-origin iframe so the plugin
 * gets full DOM/CSS/global isolation from the host while still inheriting the
 * host's session cookies (the iframe is loaded from the same origin, so the
 * browser sends the JWT cookie + CSRF cookie automatically — both on the
 * document fetch and on any AJAX the embedded page issues back to the
 * plugin's mount).
 *
 * The `sandbox` attribute carries `allow-same-origin` deliberately: without
 * it the browser would treat the iframe as an opaque origin and refuse to
 * attach the host's cookies to AJAX requests, which would break the plugin
 * auth flow.  The other allow-tokens grant the minimum the embedded page
 * needs — JS execution and form submission — and nothing else (no popups,
 * no top-frame navigation, no downloads, no pointer-lock).  `referrerPolicy`
 * is tightened to `no-referrer` so plugin requests never leak the host URL.
 *
 * The wrapper owns its own `position: relative`, so the loading overlay
 * below can never escape into a caller that forgets to set it.
 */
const PluginSurfaceFrame = (
  { url, title, sx, loadingLabel = 'Loading…' }: PluginSurfaceFrameProps
) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <Box
      sx={[{ position: 'relative', width: '100%', height: '100%' },
           ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      <Box
        component="iframe"
        src={url}
        title={title}
        sandbox="allow-scripts allow-forms allow-same-origin"
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
        sx={{
          width: '100%',
          height: '100%',
          border: 0,
          display: 'block',
          // Hidden rather than absent until the document arrives, so the
          // indicator is not layered over a flash of partial content.
          visibility: loaded ? 'visible' : 'hidden'
        }}
      />
      {!loaded && (
        <Box
          role="status"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <CircularProgress size={20} aria-hidden />
          <Typography>{loadingLabel}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default PluginSurfaceFrame;
